import { Clock, Info } from '@phosphor-icons/react';
import SecurityTag from './SecurityTag';
import { useState, useEffect } from 'react';
import WaitingExperience from './waiting-experience';
import LinkingFlowOpinion from './LinkingFlowOpinion';
import { LinkingSecurityPoints } from './LinkingFlowStep1';
import Modal from '../../../components/molecules/Modal/Modal';
import LinkingOptionButton from './LinkingOptionButton';
import PrimaryButton from '../../../components/atoms/Button';
import { getStorage } from '../../../shared/utils/general.util';
import STORAGE_KEYS from '../../../shared/constants/storage';
import FrequentAnswers from '@/components/FrequentAnswers';

const LinkingFlowStep3 = ({
	handleCancelLinking,
}: {
	handleCancelLinking: () => void;
}) => {
	const [showModal, setShowModal] = useState({
		info: false,
		cancelLinking: false,
	});

	const TOTAL_TIME_SECONDS = 60 * 10;

	const getStartTime = (): number => {
		try {
			const saved =
				JSON.parse(
					getStorage().getItem(STORAGE_KEYS.LINKING_FLOW_CONFIG) || '{}',
				)?.linkingFlowStartTime || 0;
			if (saved) {
				return parseInt(saved);
			}
		} catch (error) {}

		return Date.now();
	};

	const [startTime] = useState(getStartTime());
	const [currentTime, setCurrentTime] = useState(Date.now());

	const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
	const timeRemaining = Math.max(0, TOTAL_TIME_SECONDS - elapsedSeconds);
	const progressPercentage = Math.min(
		100,
		(elapsedSeconds / TOTAL_TIME_SECONDS) * 100,
	);

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(Date.now());
		}, 1000);

		return () => clearInterval(interval);
	}, []);

	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}min ${remainingSeconds.toString().padStart(2, '0')}s`;
	};

	return (
		<section className='px-3 py-6 max-w-[600px] mx-auto flex flex-col gap-4 fade-in-scale text-sm -mt-3 md:-mt-0'>
			<Modal
				open={showModal.cancelLinking}
				onClose={() =>
					setShowModal((prev) => ({ ...prev, cancelLinking: false }))
				}
				sx='!p-4 md:!p-4 w-[96%] max-w-lg text-sm'
			>
				<div className='flex flex-col gap-4'>
					<h3 className='text-xl text-center font-medium tracking-tight leading-tight'>
						¿Prefieres probar con otra contraseña o RFC?
					</h3>
					<p className='text-center font-light leading-tight'>
						Al hacer esto, tendrás que iniciar el proceso de conexión
						nuevamente.
					</p>
					<PrimaryButton
						label='Sí, cancelar intento de 	conexión'
						onClick={handleCancelLinking}
					/>
				</div>
			</Modal>
			<Modal
				open={showModal.info}
				onClose={() => setShowModal((prev) => ({ ...prev, info: false }))}
				sx='!p-4 md:!p-4 w-[96%] max-w-lg text-sm'
			>
				<div className='flex flex-col gap-4'>
					<h2 className='text-xl font-medium'>Conectando con el SAT...</h2>
					<p className='mb-2'>
						Dependiendo del estado de los servicios del SAT el tiempo puede ser
						mucho mayor a los 10 minutos, pero{' '}
						<b>no te preocupes seguiremos intentando realizar la conexión</b> y
						te avisaremos en cuanto este listo.
					</p>

					<LinkingOptionButton
						text={
							<p>
								<b>¿Prefieres probar con otra contraseña o RFC?</b>
								Puedes cancelar el proceso y volver a intentarlo.
							</p>
						}
						action={() => setShowModal({ info: false, cancelLinking: true })}
					/>
				</div>
			</Modal>
			<div className='bg-white rounded-xl shadow px-4 md:px-6 py-4 md:py-8 flex flex-col items-center gap-4 md:gap-6 relative overflow-hidden'>
				<SecurityTag />
				<div className='flex flex-col items-center gap-1'>
					<h2 className='text-xl font-medium'>Conectando con el SAT...</h2>
					{timeRemaining > 0 ? (
						<p className='text-h-neutral-50'>
							<Clock
								weight='fill'
								size={16}
								className='inline-block mr-1'
							/>
							Tiempo estimado {formatTime(timeRemaining)}
						</p>
					) : (
						<p
							className='text-h-primary-50 text-center cursor-pointer'
							onClick={() => setShowModal((prev) => ({ ...prev, info: true }))}
						>
							Este proceso puede tardar unos minutos más.
							<Info
								size={16}
								weight='fill'
								className='inline-block ml-0.5'
							/>
						</p>
					)}
					<div className='flex items-center gap-1 mt-2 '>
						<div className='w-44 h-4 bg-h-primary-90 rounded-full flex items-center overflow-hidden p-1'>
							<div
								className='h-2 bg-h-primary rounded-full transition-all duration-1000 ease-linear'
								style={{ width: `${progressPercentage}%` }}
							/>
						</div>
					</div>
				</div>

				<WaitingExperience />
			</div>
			<FrequentAnswers
				faqs={[
					{
						question: '¿Es seguro conectar mi cuenta del SAT aquí?',
						answer: (
							<p>
								<b>Sí, la conexión es completamente segura.</b> Utilizamos
								cifrado de última generación para proteger tu información. Solo
								tú tienes acceso a tus datos y no realizaremos ningún movimiento
								en tu cuenta del SAT sin tu consentimiento.
							</p>
						),
					},
					{
						question: '¿Qué sucede si ingreso una contraseña incorrecta?',
						answer: (
							<>
								<p>
									En caso de ingresar una contraseña incorrecta, se mostrará un
									mensaje de error indicándolo. Puedes intentarlo nuevamente las
									veces que sea necesario, ya que no existe ningún tipo de
									bloqueo o restricción en tu cuenta por intentos fallidos.
								</p>
							</>
						),
					},
					{
						question: '¿Van a hacer algo en mi cuenta del SAT?',
						answer: (
							<>
								<p>
									No. Solo consultamos tu información fiscal. No realizamos
									movimientos, trámites ni cambios.
								</p>
							</>
						),
					},
				]}
			/>

			<div className='bg-white rounded-xl shadow px-6 py-5 md:py-8 flex flex-col gap-6 relative overflow-hidden'>
				<LinkingSecurityPoints />
			</div>
			<LinkingFlowOpinion />
		</section>
	);
};

export default LinkingFlowStep3;
